import type { NormalizedLandmark } from './geometry.js';

/**
 * A single detected face, as delivered by the browser-side detector
 * (MediaPipe Face Landmarker, 478 landmarks) or by the server fallback.
 */
export interface FaceObservation {
  /** Stable index inside the observation batch, 0 = largest/most central face. */
  index: number;
  /** 468/478 normalized landmarks, image coordinates. */
  landmarks: NormalizedLandmark[];
  /** Detector confidence in [0, 1]. */
  confidence: number;
  /**
   * Optional ARKit-style blendshapes from MediaPipe. Used for eye-open and
   * expression checks when available; the geometric fallback is used otherwise.
   */
  blendshapes?: Record<string, number>;
  /**
   * Optional 4x4 facial transformation matrix (column-major, 16 numbers).
   * When present it is the authoritative source for yaw/pitch/roll.
   */
  transformationMatrix?: number[];
  /** Bounding box of the detector, normalized. */
  box?: { x: number; y: number; width: number; height: number };
}

export interface FaceObservations {
  faces: FaceObservation[];
  /** Which detector produced the observation. */
  source:
    | 'mediapipe-face-landmarker'
    | 'mediapipe-face-detector'
    | 'server-opencv'
    | 'synthetic'
    | 'unknown';
  /** Model / detector version string when available. */
  modelVersion?: string;
  /** Wall-clock cost of the detection step, milliseconds. */
  durationMs?: number;
}

export const EMPTY_FACE_OBSERVATIONS: FaceObservations = {
  faces: [],
  source: 'unknown',
};
